import { ChevronDown } from 'lucide-react';
import { useId, type SelectHTMLAttributes } from 'react';
import styles from './Select.module.css';

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & {
  label?: string;
  error?: string;
  className?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
};

/**
 * Select. `required` e apenas visual (asterisco vermelho + aria-required);
 * nao aplica o atributo HTML required.
 */
export function Select({
  label,
  error,
  className,
  id,
  options,
  placeholder,
  required,
  ...rest
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const errorId = error ? `${selectId}-error` : undefined;

  return (
    <div className={`${styles.field} ${className ?? ''}`}>
      {label ? (
        <label htmlFor={selectId} className={styles.label}>
          {label}
          {required ? (
            <>
              {' '}
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            </>
          ) : null}
        </label>
      ) : null}
      <div className={styles.wrapper}>
        <select
          id={selectId}
          className={`${styles.select} ${error ? styles.selectError : ''}`}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          aria-required={required || undefined}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown className={styles.chevron} size={18} aria-hidden />
      </div>
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
